import React, { useEffect, useMemo, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import { ApiError, api } from "../../api/apiClient";
import { RotaResponseDto } from "../types/types";

import "../../css/buttons.css";
import "../../css/cards.css";
import "../../css/forms.css";
import "./RotaResponsePage.css";

interface RouteParams {
  token: string;
}

type AvailabilityChoice = "Available" | "Unavailable";

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

export default function RotaResponsePage() {
  const { token } = useParams<RouteParams>();
  const [response, setResponse] = useState<RotaResponseDto | null>(null);
  const [choices, setChoices] = useState<Record<number, AvailabilityChoice>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<RotaResponseDto>(`/api/rota-response/${encodeURIComponent(token)}`, {
      auth: "none",
      notifyError: false,
    })
      .then(setResponse)
      .catch((err: ApiError) => {
        setError(err.status === 404 ? "Invalid token." : "Unable to load response.");
      });
  }, [token]);

  const pendingRequests = useMemo(
    () => response?.requests.filter((request) => !request.isCompleted) ?? [],
    [response],
  );

  const choose = (
    serviceInstanceAvailabilityId: number,
    status: AvailabilityChoice,
  ) => {
    setChoices((current) => ({
      ...current,
      [serviceInstanceAvailabilityId]: status,
    }));
    setError(null);
  };

  const submit = async () => {
    const missingResponse = pendingRequests.some(
      (request) => !choices[request.serviceInstanceAvailabilityId],
    );
    if (missingResponse) {
      setError("Please confirm your availability for every requested date.");
      return;
    }

    try {
      setSaving(true);
      const result = await api<RotaResponseDto>(
        `/api/rota-response/${encodeURIComponent(token)}`,
        {
          method: "POST",
          auth: "none",
          body: {
            responses: pendingRequests.map((request) => ({
              serviceInstanceAvailabilityId:
                request.serviceInstanceAvailabilityId,
              status: choices[request.serviceInstanceAvailabilityId],
            })),
          },
          notifyError: false,
        },
      );
      setResponse(result);
      setChoices({});
      setError(null);
    } catch (err) {
      const apiError = err as ApiError;
      const body = apiError.body as { message?: string } | null;
      setError(body?.message ?? "Unable to submit response.");
    } finally {
      setSaving(false);
    }
  };

  if (!response && error) {
    return (
      <div className="page-container">
        <div className="card rota-response-shell">
          <div className="card-title">{error}</div>
        </div>
      </div>
    );
  }

  if (!response) {
    return (
      <div className="page-container">
        <div className="card rota-response-shell">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="card rota-response-shell">
        <div className="card-title">{response.serviceName}</div>
        <p className="rota-response-intro">
          Hi {response.partyDisplayName}, please confirm your availability for each
          date below.
        </p>

        <div className="rota-response-dates">
          {response.requests.map((request) => {
            const start = new Date(request.startDateTime);
            const end = new Date(request.endDateTime);
            const selected = choices[request.serviceInstanceAvailabilityId];

            return (
              <section
                key={request.serviceInstanceAvailabilityId}
                className="rota-response-date"
              >
                <div className="rota-response-date-details">
                  <strong>{dateFormatter.format(start)}</strong>
                  <span>
                    {timeFormatter.format(start)} - {timeFormatter.format(end)}
                  </span>
                  <small>Role: {request.roleName}</small>
                </div>

                {request.isCompleted ? (
                  <span
                    className={`rota-response-status rota-response-status--${request.status.toLowerCase()}`}
                  >
                    {request.status}
                  </span>
                ) : (
                  <div className="rota-response-options">
                    <button
                      type="button"
                      className={`rota-response-option rota-response-option--available${
                        selected === "Available" ? " is-selected" : ""
                      }`}
                      disabled={saving}
                      onClick={() =>
                        choose(request.serviceInstanceAvailabilityId, "Available")
                      }
                    >
                      <CheckCircleIcon fontSize="small" />
                      Available
                    </button>
                    <button
                      type="button"
                      className={`rota-response-option rota-response-option--unavailable${
                        selected === "Unavailable" ? " is-selected" : ""
                      }`}
                      disabled={saving}
                      onClick={() =>
                        choose(
                          request.serviceInstanceAvailabilityId,
                          "Unavailable",
                        )
                      }
                    >
                      <CloseIcon fontSize="small" />
                      Unavailable
                    </button>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {error && <div className="error rota-response-error">{error}</div>}

        {response.isCompleted ? (
          <div className="success-message rota-response-success">
            Thank you. Your responses have been recorded.
          </div>
        ) : (
          <button
            type="button"
            className="action-button primary rota-response-submit"
            disabled={saving}
            onClick={submit}
          >
            Submit availability
          </button>
        )}
      </div>
    </div>
  );
}
